// SolyticsPopper.jsx
// @ts-nocheck

import React from 'react';
import { Popper as MuiPopper, ClickAwayListener, Fade } from '@mui/material';

const DEFAULT_OPEN = false;
const DEFAULT_PLACEMENT = 'bottom';
const DEFAULT_DISABLE_PORTAL = false;
const DEFAULT_KEEP_MOUNTED = false;
const DEFAULT_ENABLE_CLICK_AWAY = false;
const DEFAULT_FADE_TIMEOUT = 150;

/**
 * SolyticsPopper - A customizable popper component built on top of MUI Popper.
 *
 * This component provides an enhanced popper with additional features including:
 * - Optional click-away listener functionality
 * - Smooth fade transitions
 * - Flexible positioning and portal control
 * - Theme-aware styling
 *
 * @component
 * @example
 * // Basic usage
 * <SolyticsPopper
 *   open={isOpen}
 *   anchorEl={anchorElement}
 *   placement="bottom-start"
 * >
 *   <div>Popper content</div>
 * </SolyticsPopper>
 *
 * @example
 * // With click-away and transition
 * <SolyticsPopper
 *   open={isOpen}
 *   anchorEl={anchorElement}
 *   enableClickAway={true}
 *   onClickAway={() => setIsOpen(false)}
 *   transition={true}
 * >
 *   <div>Popper content</div>
 * </SolyticsPopper>
 *
 * @param {Object} props - Component props
 * @param {React.ReactNode} props.children - Content to display inside the popper
 * @param {boolean} [props.open=false] - Whether the popper is visible
 * @param {HTMLElement|null} props.anchorEl - The anchor element for positioning the popper
 * @param {string} [props.placement='bottom'] - Popper placement relative to anchor (e.g., 'top', 'bottom', 'left', 'right')
 * @param {boolean} [props.disablePortal=false] - If true, renders popper in the DOM hierarchy where it's declared
 * @param {boolean} [props.keepMounted=false] - If true, keeps the popper in the DOM when closed
 * @param {boolean} [props.enableClickAway=false] - If true, enables click-away detection to close popper
 * @param {Function} props.onClickAway - Callback when clicking outside the popper (required when enableClickAway is true)
 * @param {Array} [props.modifiers] - Popper.js modifiers for advanced positioning control
 * @param {boolean} [props.transition=false] - If true, enables fade transition animation
 * @param {Object} [props.transitionProps] - Additional props for the Fade transition component
 * @param {number} [props.transitionProps.timeout=150] - Transition duration in milliseconds
 * @param {Object|Function} [props.sx] - MUI sx prop for custom styling
 * @returns {React.ReactElement} The rendered popper component
 */
const SolyticsPopper = ({
  children,
  open = DEFAULT_OPEN,
  anchorEl,
  placement = DEFAULT_PLACEMENT,
  disablePortal = DEFAULT_DISABLE_PORTAL,
  keepMounted = DEFAULT_KEEP_MOUNTED,
  enableClickAway = DEFAULT_ENABLE_CLICK_AWAY,
  onClickAway,
  modifiers,
  transition = false,
  transitionProps,
  sx,
}) => {
  // Validate that onClickAway is provided when enableClickAway is true
  if (enableClickAway && !onClickAway) {
    console.warn(
      'SolyticsPopper: onClickAway callback is required when enableClickAway is true. ' +
        'Click-away functionality will not work without this handler.'
    );
  }

  const effectiveKeepMounted = transition ? true : keepMounted;

  const popperProps = {
    open,
    anchorEl,
    placement,
    disablePortal,
    keepMounted: effectiveKeepMounted,
    ...(modifiers && { modifiers }),
    ...(transition ? { transition: true } : {}),
    sx: (theme) => ({
      zIndex: theme.zIndex.tooltip,
      ...(typeof sx === 'function' ? sx(theme) : sx),
    }),
  };

  const handleClickAway = (event) => {
    // Click-away only matters when enabled and popper is open
    if (!enableClickAway || !open) return;

    // Ignore clicks on the anchor element itself (open/close toggle)
    if (anchorEl && anchorEl.contains(event.target)) return;

    onClickAway?.(event);
  };

  // Decide when ClickAwayListener should be active
  const shouldWrapWithClickAway = enableClickAway && (transition || open);

  // Render content with minimal DOM nesting
  const renderContent = () => {
    if (transition) {
      return (
        <MuiPopper {...popperProps}>
          {({ TransitionProps }) => {
            const fadeContent = (
              <Fade
                {...TransitionProps}
                timeout={transitionProps?.timeout ?? DEFAULT_FADE_TIMEOUT}
                {...transitionProps}
              >
                <div>{children}</div>
              </Fade>
            );

            return shouldWrapWithClickAway ? (
              <ClickAwayListener onClickAway={handleClickAway}>
                {fadeContent}
              </ClickAwayListener>
            ) : (
              fadeContent
            );
          }}
        </MuiPopper>
      );
    }

    const content = shouldWrapWithClickAway ? (
      <ClickAwayListener onClickAway={handleClickAway}>
        <div>{children}</div>
      </ClickAwayListener>
    ) : (
      children
    );

    return <MuiPopper {...popperProps}>{content}</MuiPopper>;
  };

  return renderContent();
};

export default SolyticsPopper;
